import React from "react";
import Drawer from "@mui/material/Drawer";
import DrawerHeader from "../../requests/shared/helpers/DrawerHeader";
import DrawerFooterContainerLarge from "../../requests/shared/helpers/DrawerFooterContainerLarge";
import MenuItem from "@mui/material/MenuItem";
import ListSubheader from "@mui/material/ListSubheader";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import Autocomplete from "@mui/material/Autocomplete";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import AddIcon from "@mui/icons-material/Add";
import { formatName, PurpleSwitch } from "../../../../helpers/helpers";
import {
  AUTOCOMPLETE_TEXTFIELD_SX,
  CHECKBOX_SX,
  DrawerLabel,
  DrawerSaveFooter,
  INPUT_SX,
  SELECT_SX,
} from "./drawerInputs";

const SOURCE_GROUP_LABELS = {
  daily_stats: "Store data",
  reporting: "More store metrics",
  compliance: "Labor law violations",
  labor_split: "Labor by type",
  departments: "Labor by department",
  log_categories: "Numeric log categories",
};

const COLUMN_OPTIONS = [
  ["value", "Value"],
  ["vs_last_week", "vs last week"],
  ["vs_target", "vs target"],
  ["status", "Status"],
];

// Settings for a metric table block: pick the rows (each an admin-chosen
// metric with its own optional flag target) and the columns.
function TableBlockDrawer(props) {
  const [config, setConfig] = React.useState({});
  const [notifyEnabled, setNotifyEnabled] = React.useState(false);

  React.useEffect(() => {
    if (props.open) {
      const next = { ...(props.block?.config || {}) };
      next.rows = (next.rows || []).map((row) => ({ ...row }));
      next.columns = next.columns || ["value", "vs_last_week", "status"];
      setConfig(next);
      setNotifyEnabled((next.notify_user_ids || []).length > 0);
    }
  }, [props.open, props.block]);

  const setField = (name, value) =>
    setConfig((prev) => ({ ...prev, [name]: value }));

  const setRowField = (index, name, value) => {
    setConfig((prev) => {
      const rows = prev.rows.map((row, i) =>
        i === index ? { ...row, [name]: value } : row
      );
      return { ...prev, rows };
    });
  };

  const sources = props.sources || [];
  const groupedSources = sources
    .filter((s) => s.key !== "weather")
    .reduce((groups, source) => {
      (groups[source.group] = groups[source.group] || []).push(source);
      return groups;
    }, {});
  const notifyUsers = (props.notifyOptions || []).filter((u) =>
    (config.notify_user_ids || []).includes(parseInt(u.id))
  );

  return (
    <Drawer anchor="right" open={props.open} onClose={props.onClose}>
      <div className="w-[500px] h-full bg-slate-50 relative">
        <div className="max-h-screen overflow-auto">
          <DrawerHeader title="Metric Table" onClose={props.onClose} />
          <div className="w-full px-6">
            <p className="text-slate-500 text-sm -mt-6">
              Pick the rows; each row can carry its own flag target
            </p>

            <div className="mt-7">
              <DrawerLabel>Table name</DrawerLabel>
              <div className="mt-1">
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="Store metrics"
                  value={config.label || ""}
                  onChange={(e) => setField("label", e.target.value || null)}
                  InputProps={{ sx: INPUT_SX }}
                />
              </div>
            </div>

            <div className="mt-7">
              <DrawerLabel>Columns</DrawerLabel>
              <div className="mt-1 bg-white border border-slate-200 rounded-lg">
                {COLUMN_OPTIONS.map(([key, label], index) => {
                  const isOn = (config.columns || []).includes(key);
                  return (
                    <div
                      key={key}
                      className={`flex items-center px-2 py-0.5 cursor-pointer ${
                        index > 0 ? "border-t border-slate-100" : ""
                      }`}
                      onClick={() =>
                        setField(
                          "columns",
                          isOn
                            ? config.columns.filter((c) => c !== key)
                            : [...config.columns, key]
                        )
                      }
                    >
                      <Checkbox
                        checked={isOn}
                        size="small"
                        sx={CHECKBOX_SX}
                      />
                      <p className="text-sm text-slate-700">{label}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="mt-7">
              <DrawerLabel>Rows</DrawerLabel>
              <div className="flex flex-col gap-3 mt-1">
                {(config.rows || []).map((row, index) => (
                  <div
                    key={index}
                    className="bg-white border border-slate-200 rounded-xl p-3"
                  >
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <Select
                          fullWidth
                          size="small"
                          value={row.source || ""}
                          displayEmpty
                          onChange={(e) => setRowField(index, "source", e.target.value)}
                          sx={SELECT_SX}
                        >
                          <MenuItem value="" disabled>
                            Pick a metric
                          </MenuItem>
                          {Object.entries(groupedSources).flatMap(
                            ([group, groupSources]) => [
                              <ListSubheader key={`h-${group}`}>
                                {SOURCE_GROUP_LABELS[group] || group}
                              </ListSubheader>,
                              ...groupSources.map((source) => (
                                <MenuItem key={source.key} value={source.key}>
                                  {source.label}
                                </MenuItem>
                              )),
                            ]
                          )}
                        </Select>
                      </div>
                      <div
                        className="w-8 h-8 shrink-0 rounded-lg bg-slate-100 flex items-center justify-center cursor-pointer hover:bg-slate-200"
                        onClick={() =>
                          setField(
                            "rows",
                            config.rows.filter((_, i) => i !== index)
                          )
                        }
                      >
                        <DeleteOutlinedIcon
                          style={{ fontSize: "16px", color: "#64748B" }}
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <TextField
                        variant="outlined"
                        size="small"
                        type="number"
                        className="w-32"
                        placeholder="Target"
                        value={row.target ?? ""}
                        onChange={(e) =>
                          setRowField(
                            index,
                            "target",
                            e.target.value === "" ? null : e.target.value
                          )
                        }
                        InputProps={{ sx: INPUT_SX }}
                      />
                      {row.target !== null &&
                        row.target !== undefined &&
                        row.target !== "" && (
                          <div className="flex-1">
                            <Select
                              fullWidth
                              size="small"
                              value={row.direction || "flag_below"}
                              onChange={(e) =>
                                setRowField(index, "direction", e.target.value)
                              }
                              sx={SELECT_SX}
                            >
                              <MenuItem value="flag_above">Flag above target</MenuItem>
                              <MenuItem value="flag_below">Flag below target</MenuItem>
                            </Select>
                          </div>
                        )}
                    </div>
                  </div>
                ))}
                <div
                  className="flex items-center justify-center gap-1.5 py-2.5 border border-dashed border-slate-300 rounded-xl cursor-pointer text-slate-500 hover:border-violet-400 hover:text-violet-700"
                  onClick={() =>
                    setField("rows", [
                      ...(config.rows || []),
                      { source: "", target: null, direction: "flag_below" },
                    ])
                  }
                >
                  <AddIcon style={{ fontSize: "16px" }} />
                  <p className="text-xs font-medium">Add row</p>
                </div>
              </div>
            </div>

            <div className="mt-7">
              <div className="flex items-center justify-between">
                <p className="text-slate-500 text-sm">
                  Require a manager response when any row is flagged?
                </p>
                <div className="ml-4">
                  <PurpleSwitch
                    checked={Boolean(config.require_response)}
                    onChange={(e) => setField("require_response", e.target.checked)}
                  />
                </div>
              </div>
              <p className="text-xs text-slate-400 mt-1">
                One response entry on the table covers the day; the log shows
                it as outstanding until someone explains the flags.
              </p>
            </div>

            <div className="mt-7">
              <div className="flex items-center justify-between">
                <p className="text-slate-500 text-sm">
                  Do you want flagged rows to notify specific people?
                </p>
                <div className="ml-4">
                  <PurpleSwitch
                    checked={notifyEnabled}
                    onChange={(e) => {
                      setNotifyEnabled(e.target.checked);
                      if (!e.target.checked) {
                        setField("notify_user_ids", []);
                      }
                    }}
                  />
                </div>
              </div>
              {notifyEnabled && (
                <div className="mt-2">
                  <Autocomplete
                    multiple
                    fullWidth
                    disableClearable
                    disableCloseOnSelect
                    options={props.notifyOptions || []}
                    getOptionLabel={(option) => formatName(option.name)}
                    value={notifyUsers}
                    onChange={(event, newValue) =>
                      setField(
                        "notify_user_ids",
                        newValue.map((u) => parseInt(u.id))
                      )
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder="Select members to notify"
                        size="small"
                        sx={AUTOCOMPLETE_TEXTFIELD_SX}
                      />
                    )}
                    renderOption={(optionProps, option, { selected }) => (
                      <li {...optionProps} key={option.id}>
                        <Checkbox
                          style={{ marginRight: 8 }}
                          checked={selected}
                          sx={CHECKBOX_SX}
                        />
                        {formatName(option.name)}
                      </li>
                    )}
                  />
                </div>
              )}
            </div>

          </div>
          <DrawerFooterContainerLarge>
            <DrawerSaveFooter
              disabled={
                !config.rows?.length ||
                config.rows.some((row) => !row.source) ||
                !config.columns?.length
              }
              onSave={() => {
                props.onSave({
                  ...config,
                  rows: (config.rows || []).filter((row) => row.source),
                });
                props.onClose();
              }}
              onClose={props.onClose}
            />
          </DrawerFooterContainerLarge>
          <div className="h-[200px]"></div>
        </div>
      </div>
    </Drawer>
  );
}

export default TableBlockDrawer;
